import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BadgeCheck, Bike, CalendarDays, Check, Dumbbell, ExternalLink, Flag, Link2,
  MoreHorizontal, Moon, Mountain, Pencil, X, Plus, Route, Trash2, Users,
  type LucideIcon,
} from 'lucide-react';
import {
  ChallengeBikeType, CHALLENGE_TEXTS, ChallengeBoard, ChallengeBoardKind, ChallengeJoinDraft, ChallengeBoardRow, ChallengeMe, ChallengeRide,
  ChallengeRideDraft, ChallengeSummary, CHALLENGE_BIKE_LABELS, CHALLENGE_BIKE_TYPES, CHALLENGE_MILESTONES, crossedMilestone, milestoneTrackPercent,
  REGION_GROUPS, boardRowDetail, boardRowName, challengePhase, displayBundesland, formatDayShort,
  challengeActivity, challengeEditable, challengeEmptyTexts, challengeGoalModel, challengeRules, formatDayWeekdayShort, formatKm, formatKmShort, progressPercent, proofLinkLabel,
  seasonRangeLabel, startInLabel, validateDisplayName, validateJoinDraft, validateRideDraft,
} from '@radevents/shared';
import { ChallengeArt, CountUp, MilestoneBurst, useFreshMilestone, useGrow } from './Motion';
import { useDialogFocus } from './useDialogFocus';
import { ORGANIZER_ACCESS_CSS } from '../organizer/OrganizerAccessDialog';
import './winterchallenge.css';

/**
 * Bausteine der Winterchallenge (`docs/bereiche/winterchallenge.md`). Jeder Baustein bekommt
 * seine Daten und `today` von außen, damit das Design-Lab vor, während und nach der Saison
 * nebeneinander zeigen kann.
 */

const BIKE_ICONS: Record<ChallengeBikeType, LucideIcon> = {
  ROAD: Bike,
  GRAVEL: Route,
  MTB: Mountain,
  TOUR: Bike,
  INDOOR: Dumbbell,
};

// ── Kopf ──────────────────────────────────────────────────────────────────────────────────────

export function ChallengeHero({ summary, today, joined, loggedIn = true, onJoin }: {
  summary: ChallengeSummary;
  today: string;
  joined: boolean;
  /** Nicht angemeldet: der Knopf führt erst zur Anmeldung. */
  loggedIn?: boolean;
  onJoin: () => void;
  /** Teilnehmer tragen in „Meine Winterchallenge“ ein; der Kopf zeigt ihnen keinen Knopf. */
  onAddRide?: () => void;
}) {
  const phase = challengePhase(summary, today);
  const lead = phase === 'after'
    ? 'Die Saison ist vorbei. Danke an alle, die mitgefahren sind.'
    : 'Sammle über den Winter Kilometer, allein oder im Team. Jeder Kilometer zählt für dich, dein Team und deine Region.';
  return (
    <section className={`wc-hero${joined ? ' wc-hero--joined' : ''}`}>
      <div className="wc-hero-text">
        <span className="wc-kicker">{seasonRangeLabel(summary)}</span>
        <h1 className="wc-h1">{summary.title}</h1>
        {!joined && <p className="wc-lead">{lead}</p>}
        {phase !== 'after' && !joined && (
          <div className="wc-hero-actions">
            <button type="button" className="wc-btn wc-cue" onClick={onJoin}>{loggedIn ? 'Mitmachen' : 'Anmelden und mitmachen'}<ArrowRight size={17} aria-hidden /></button>
            {phase === 'before' && (
              <span className="wc-hero-note">
                <CalendarDays size={15} aria-hidden />
                {startInLabel(summary, today)}
              </span>
            )}
          </div>
        )}
        {summary.sponsor && (
          <a className="wc-sponsor" href={summary.sponsor.url} target="_blank" rel="noopener noreferrer sponsored">
            <span>präsentiert von</span>
            {summary.sponsor.logoUrl
              ? <img src={summary.sponsor.logoUrl} alt={summary.sponsor.name} />
              : <strong>{summary.sponsor.name}</strong>}
          </a>
        )}
      </div>
      {!joined && <ChallengeArt className="wc-hero-art" />}
      <CommunityProgress summary={summary} today={today} />
    </section>
  );
}

export function CommunityProgress({ summary, today }: { summary: ChallengeSummary; today: string }) {
  const phase = challengePhase(summary, today);
  const pct = useGrow(progressPercent(summary.totalKm, summary.goalKm));
  const reached = summary.totalKm >= summary.goalKm;
  const goal = challengeGoalModel(summary, today);
  return (
    <div className="wc-community">
      <div className="wc-community-head">
        <span className="wc-community-label">
          {goal.moon && <Moon size={14} aria-hidden />}{goal.label}
        </span>
        <span className="wc-community-value">
          <strong><CountUp value={summary.totalKm} format={formatKmShort} /> km</strong>
          <span> von {formatKmShort(summary.goalKm)} km</span>
          {reached && <span className="wc-chip wc-chip--done"><Check size={13} aria-hidden />geschafft</span>}
        </span>
      </div>
      <div className="wc-bar" role="progressbar" aria-valuemin={0} aria-valuemax={summary.goalKm}
        aria-valuenow={summary.totalKm} aria-label="Kilometer der Community">
        <div className="wc-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      {phase === 'before' && summary.totalKm === 0 && (
        <p className="wc-muted">Gezählt wird ab dem {formatDayShort(summary.startsOn)}.</p>
      )}
      <div className="wc-stats">
        <Stat icon={Users} value={summary.participants} label={summary.participants === 1 ? 'fährt mit' : 'fahren mit'} />
        <Stat icon={Flag} value={summary.teams} label={summary.teams === 1 ? 'Team' : 'Teams'} />
        <Stat icon={Route} value={summary.countries} label={summary.countries === 1 ? 'Land' : 'Länder'} />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label }: { icon: LucideIcon; value: number; label: string }) {
  return (
    <div className="wc-stat">
      <Icon size={16} aria-hidden />
      <strong>{value.toLocaleString('de-DE')}</strong>
      <span>{label}</span>
    </div>
  );
}

// ── Meine Winterchallenge ─────────────────────────────────────────────────────────────────────

export function MyChallenge({ me, summary, today, formOpen, onAddRide, onEditRide, onDeleteRide, form, nameForm, onEditName }: {
  me: ChallengeMe;
  summary: ChallengeSummary;
  today: string;
  formOpen?: boolean;
  onAddRide: () => void;
  onEditRide?: (ride: ChallengeRide) => void;
  onDeleteRide?: (ride: ChallengeRide) => void;
  /** Das Formular, wenn offen: steht an Stelle des Knopfs, nicht in einem Fenster darüber. */
  form?: React.ReactNode;
  /** Anzeigename in Bearbeitung: das Formular steht an Stelle des Namens. */
  nameForm?: React.ReactNode;
  onEditName?: () => void;
}) {
  const editable = challengeEditable(summary, today);
  const before = challengePhase(summary, today) === 'before';
  return (
    <section className="wc-card wc-me" aria-labelledby="wc-me-title">
      <div className="wc-me-head">
        <div style={{ minWidth: 0, flex: 1 }}>
          <span className="wc-kicker">Meine Winterchallenge</span>
          {nameForm ?? (
            <h2 id="wc-me-title" className="wc-me-name">
              {me.displayName}
              {editable && onEditName && (
                <button type="button" className="wc-icon-btn wc-me-name-edit" aria-label="Anzeigenamen ändern" onClick={onEditName}>
                  <Pencil size={15} aria-hidden />
                </button>
              )}
            </h2>
          )}
          <div className="wc-me-sub">
            {displayBundesland(me.bundesland)}
            {me.team && <> · <Link to={`/winterchallenge/team/${me.team.slug}`}>{me.team.name}</Link></>}
          </div>
        </div>
        {/* Vor dem Start gibt es noch nichts zu zählen; eine Null hier stünde nur herum. */}
        {!before && (
          <div className="wc-me-numbers">
            <div><strong><CountUp value={me.km} ms={800} format={n => formatKm(Math.round(n * 10) / 10)} /></strong><span>km</span></div>
            {me.rank != null && <div><strong>{me.rank.toLocaleString('de-DE')}</strong><span>Platz</span></div>}
          </div>
        )}
      </div>

      {editable && (formOpen ? form : (
        <button type="button" className="wc-btn wc-btn--block wc-cue" onClick={onAddRide}>
          <Plus size={17} aria-hidden />Kilometer eintragen
        </button>
      ))}

      {/* Vor dem Start stünden hier nur Nullen und „Im Oktober“. */}
      {!before && <ActivityProgress me={me} summary={summary} today={today} />}
      <Milestones km={me.km} />

      <RideList rides={me.rides} summary={summary} before={before} onEdit={editable ? onEditRide : undefined} onDelete={editable ? onDeleteRide : undefined} />
    </section>
  );
}

/** Anzeigenamen ändern, an der Stelle des Namens in „Meine Winterchallenge“. */
export function NameForm({ initial, saving = false, serverError, onSubmit, onCancel }: {
  initial: string;
  saving?: boolean;
  serverError?: string | null;
  onSubmit: (displayName: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial);
  const [tried, setTried] = useState(false);
  const error = tried ? validateDisplayName(name) : null;
  return (
    <form className="wc-name-form" noValidate onSubmit={e => {
      e.preventDefault();
      setTried(true);
      if (!validateDisplayName(name)) onSubmit(name.trim().replace(/\s+/g, ' '));
    }}>
      <label className="wc-field">
        <span>Anzeigename</span>
        <input id="wc-me-title" value={name} maxLength={40} autoFocus aria-invalid={!!error}
          onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') onCancel(); }} />
        {error
          ? <small className="wc-error">{error}</small>
          : <small className="wc-hint">So stehst du in der Rangliste.</small>}
      </label>
      {serverError && <p className="wc-error" role="alert">{serverError}</p>}
      <div className="wc-form-actions">
        <button type="button" className="wc-btn wc-btn--ghost" onClick={onCancel}>Abbrechen</button>
        <button type="submit" className="wc-btn" disabled={saving}>{saving ? 'Speichert …' : 'Speichern'}</button>
      </div>
    </form>
  );
}

export function Milestones({ km }: { km: number }) {
  // Fällt beim Eintragen auf der Seite ein Meilenstein, feiert sein Punkt; das Schnellfenster feiert am Pokal.
  const fresh = useFreshMilestone(km, crossedMilestone);
  // Die Linie zwischen den Punkten wächst mit, vom ersten Zeichnen an und bei jeder neuen Fahrt.
  const track = useGrow(milestoneTrackPercent(km));
  return (
    <ol className="wc-milestones" aria-label="Meilensteine" style={{ ['--track' as string]: track / 100 } as React.CSSProperties}>
      {CHALLENGE_MILESTONES.map((m, i) => {
        const done = km >= m;
        return (
          <li key={m} className={`${done ? 'is-done' : ''}${m === fresh ? ' is-fresh' : ''}` || undefined} style={{ ['--i' as string]: i } as React.CSSProperties}>
            <span className="wc-burst-host">
              <span key={m === fresh ? 'fresh' : 'dot'} className="wc-milestone-dot">{done ? <Check size={14} aria-hidden /> : null}</span>
              {m === fresh && <MilestoneBurst radius={46} count={14} />}
            </span>
            <span className="wc-milestone-label">{formatKm(m)}</span>
            <span className="sr-only">{done ? 'erreicht' : 'offen'}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function RideList({ rides, summary, before = false, onEdit, onDelete }: {
  rides: ChallengeRide[];
  summary: Pick<ChallengeSummary, 'startsOn'>;
  /** Vor dem Start der Saison: sagt, ab wann eingetragen werden kann. */
  before?: boolean;
  onEdit?: (ride: ChallengeRide) => void;
  onDelete?: (ride: ChallengeRide) => void;
}) {
  if (rides.length === 0) {
    const texts = challengeEmptyTexts(summary);
    return <p className="wc-empty">{before ? texts.noRidesBefore : texts.noRides}</p>;
  }
  return (
    <ul className="wc-rides">
      {rides.map(r => {
        const Icon = BIKE_ICONS[r.bikeType];
        const proof = proofLinkLabel(r.proofUrl);
        return (
          <li key={r.id} className="wc-ride">
            <span className="wc-ride-icon"><Icon size={16} aria-hidden /></span>
            <div className="wc-ride-main">
              <strong>{formatDayWeekdayShort(r.rideDate)}</strong>
              <span>
                {CHALLENGE_BIKE_LABELS[r.bikeType]}
                {proof && r.proofUrl && <> · <a href={r.proofUrl} target="_blank" rel="noopener noreferrer">{proof}<ExternalLink size={11} aria-hidden /></a></>}
              </span>
            </div>
            <span className="wc-ride-km">{formatKm(r.km)} km</span>
            {(onEdit || onDelete) && (
              <span className="wc-ride-actions">
                {onEdit && <button type="button" className="wc-icon-btn" aria-label="Fahrt bearbeiten" onClick={() => onEdit(r)}><Pencil size={15} aria-hidden /></button>}
                {onDelete && <button type="button" className="wc-icon-btn" aria-label="Fahrt löschen" onClick={() => onDelete(r)}><Trash2 size={15} aria-hidden /></button>}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ── Kilometer eintragen ───────────────────────────────────────────────────────────────────────

export function RideForm({ summary, today, initial, kmSameDay = 0, showErrors = false, saving = false, serverError, inDialog = false, onDateChange, onSubmit, onCancel }: {
  summary: ChallengeSummary;
  today: string;
  initial?: Partial<ChallengeRideDraft>;
  kmSameDay?: number;
  /** Datum geändert: die Seite rechnet die Tagessumme für diesen Tag neu. */
  onDateChange?: (rideDate: string) => void;
  /** Design-Lab: Fehler sofort zeigen statt erst nach dem ersten Absenden. */
  showErrors?: boolean;
  saving?: boolean;
  serverError?: string | null;
  /** Im Schnellfenster (`QuickRideDialog`): ohne eigenen Kasten. */
  inDialog?: boolean;
  onSubmit: (draft: ChallengeRideDraft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<ChallengeRideDraft>({
    rideDate: today > summary.endsOn ? summary.endsOn : today, km: '', bikeType: null, proofUrl: '', ...initial,
  });
  const [tried, setTried] = useState(showErrors);
  const errors = useMemo(() => validateRideDraft(draft, summary, today, kmSameDay), [draft, summary, today, kmSameDay]);
  const shown = tried ? errors : {};
  const set = <K extends keyof ChallengeRideDraft>(k: K, v: ChallengeRideDraft[K]) => setDraft(d => ({ ...d, [k]: v }));
  const proof = proofLinkLabel(draft.proofUrl);
  const maxDate = today < summary.endsOn ? today : summary.endsOn;

  return (
    <form className={inDialog ? 'wc-form wc-form--dialog' : 'wc-form ui-aufklappen'} noValidate onSubmit={e => {
      e.preventDefault();
      setTried(true);
      if (Object.keys(errors).length === 0) onSubmit(draft);
    }}>
      <div className="wc-form-row">
        <label className="wc-field">
          <span>Datum</span>
          {/* Im Fenster setzt der Dialog den Fokus selbst; auf der Seite steht das Formular an Stelle des Knopfs. */}
          <input type="date" autoFocus={!inDialog} value={draft.rideDate} min={summary.startsOn} max={maxDate}
            aria-invalid={!!shown.rideDate} onChange={e => { set('rideDate', e.target.value); onDateChange?.(e.target.value); }} />
          {shown.rideDate && <small className="wc-error">{shown.rideDate}</small>}
        </label>
        <label className="wc-field">
          <span>Kilometer</span>
          <span className="wc-input-suffix">
            <input inputMode="decimal" placeholder="48" value={draft.km}
              aria-invalid={!!shown.km} onChange={e => set('km', e.target.value)} />
            <em>km</em>
          </span>
          {shown.km && <small className="wc-error">{shown.km}</small>}
        </label>
      </div>

      <fieldset className="wc-field">
        <legend>Womit</legend>
        <div className="wc-pills" role="radiogroup">
          {CHALLENGE_BIKE_TYPES.map(t => {
            const Icon = BIKE_ICONS[t];
            const on = draft.bikeType === t;
            return (
              <button key={t} type="button" role="radio" aria-checked={on}
                className={`wc-pill${on ? ' is-on' : ''}`} onClick={() => set('bikeType', t)}>
                <Icon size={15} aria-hidden />{CHALLENGE_BIKE_LABELS[t]}
              </button>
            );
          })}
        </div>
        {shown.bikeType && <small className="wc-error">{shown.bikeType}</small>}
      </fieldset>

      <label className="wc-field">
        <span>Link zur Aktivität <i>optional</i></span>
        <span className={`wc-input-icon${proof ? ' has-host' : ''}`}>
          <Link2 size={15} aria-hidden />
          <input type="url" placeholder="https://…" value={draft.proofUrl}
            aria-invalid={!!shown.proofUrl} onChange={e => set('proofUrl', e.target.value)} />
          {proof && <em className="wc-proof-host"><Check size={13} aria-hidden />{proof}</em>}
        </span>
        {shown.proofUrl
          ? <small className="wc-error">{shown.proofUrl}</small>
          : <small className="wc-hint">{CHALLENGE_TEXTS.proofHint}</small>}
      </label>

      {serverError && <p className="wc-error" role="alert">{serverError}</p>}

      <div className="wc-form-actions">
        <button type="button" className="wc-btn wc-btn--ghost" onClick={onCancel}>Abbrechen</button>
        <button type="submit" className="wc-btn" disabled={saving}>{saving ? 'Speichert …' : 'Speichern'}</button>
      </div>
    </form>
  );
}

// ── Ranglisten ────────────────────────────────────────────────────────────────────────────────

const BOARD_TABS: { kind: ChallengeBoardKind; label: string }[] = [
  { kind: 'riders', label: 'Einzel' },
  { kind: 'teams', label: 'Teams' },
  { kind: 'regions', label: 'Bundesländer' },
  { kind: 'countries', label: 'Länder' },
];


export function Leaderboard({ boards, unavailable = {}, summary, initialKind = 'riders', onShowAll, onReport, onKindChange }: {
  boards: Partial<Record<ChallengeBoardKind, ChallengeBoard>>;
  /** Wertungen, deren Abruf gescheitert ist: „nicht erreichbar“ statt „noch niemand“. */
  unavailable?: Partial<Record<ChallengeBoardKind, boolean>>;
  /** Für den Leertext mit dem Starttag; ohne Saison ein allgemeiner Satz. */
  summary?: Pick<ChallengeSummary, 'startsOn'> | null;
  initialKind?: ChallengeBoardKind;
  onShowAll?: (kind: ChallengeBoardKind) => void;
  /** Angemeldet: eine Fahrerzeile melden (Fair Play). */
  onReport?: (row: ChallengeBoardRow) => void;
  /** Reiter gewechselt, etwa um die Wertung nachzuladen. */
  onKindChange?: (kind: ChallengeBoardKind) => void;
}) {
  const [kind, setKind] = useState<ChallengeBoardKind>(initialKind);
  const board = boards[kind];
  const rows = board?.rows ?? [];

  return (
    <section className="wc-card" aria-labelledby="wc-board-title">
      <h2 id="wc-board-title" className="wc-h2">Rangliste</h2>
      <div className="wc-tabs" role="tablist">
        {BOARD_TABS.map(t => (
          <button key={t.kind} type="button" role="tab" aria-selected={kind === t.kind}
            className={`wc-pill${kind === t.kind ? ' is-on' : ''}`} onClick={() => { setKind(t.kind); onKindChange?.(t.kind); }}>
            {t.label}
          </button>
        ))}
      </div>

      {unavailable[kind] ? (
        <p className="wc-empty" role="alert">{CHALLENGE_TEXTS.boardUnavailable}</p>
      ) : !board || rows.length === 0 ? (
        <p className="wc-empty">{summary ? challengeEmptyTexts(summary).emptyBoard : 'Noch niemand in der Wertung.'}</p>
      ) : (
        <>
          <ol className="wc-board">
            {rows.map(r => <BoardRow key={`${r.rank}-${r.name}`} kind={kind} row={r} onReport={onReport} />)}
            {board.me && !rows.some(r => r.isMe) && (
              <>
                <li className="wc-board-gap" aria-hidden><MoreHorizontal size={18} /></li>
                <BoardRow kind={kind} row={board.me} />
              </>
            )}
          </ol>
          {onShowAll && board.total > rows.length && (
            <button type="button" className="wc-link" onClick={() => onShowAll(kind)}>
              Alle {board.total.toLocaleString('de-DE')} anzeigen
            </button>
          )}
        </>
      )}
    </section>
  );
}

function BoardRow({ kind, row, onReport }: { kind: ChallengeBoardKind; row: ChallengeBoardRow; onReport?: (row: ChallengeBoardRow) => void }) {
  const detail = boardRowDetail(kind, row);
  const name = boardRowName(kind, row);
  return (
    <li className={`wc-board-row${row.isMe ? ' is-me' : ''}${row.rank <= 3 ? ' is-top' : ''}`}>
      <span className="wc-rank">{row.rank.toLocaleString('de-DE')}</span>
      <div className="wc-board-main">
        <strong>
          {kind === 'teams' && row.slug ? <Link to={`/winterchallenge/team/${row.slug}`} className="wc-board-link">{name}</Link> : name}
          {row.verified && <BadgeCheck size={15} aria-label="Verein" className="wc-verified" />}
          {row.isMe && <span className="wc-chip">{kind === 'riders' ? 'du' : 'deins'}</span>}
        </strong>
        {detail && <span>{detail}</span>}
      </div>
      <span className="wc-board-km">{formatKm(row.km)}<small> km</small></span>
      {onReport && kind === 'riders' && row.id != null && !row.isMe && (
        <button type="button" className="wc-icon-btn wc-report" aria-label={`${name} melden`} title="Melden" onClick={() => onReport(row)}>
          <Flag size={14} aria-hidden />
        </button>
      )}
    </li>
  );
}

// ── Mitmachen ─────────────────────────────────────────────────────────────────────────────────

export type JoinDraft = ChallengeJoinDraft;

export function JoinForm({ initial, showErrors = false, serverError, saving = false, inDialog = false, onSubmit, onCancel }: {
  initial?: Partial<JoinDraft>;
  showErrors?: boolean;
  serverError?: string | null;
  saving?: boolean;
  /** Im Dialog: ohne eigene Karte und Überschrift, die trägt der Dialog. */
  inDialog?: boolean;
  onSubmit: (draft: JoinDraft) => void;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState<JoinDraft>({ displayName: '', bundesland: '', ...initial, consent: true });
  const [tried, setTried] = useState(showErrors);
  const errors = validateJoinDraft(draft);
  const ok = Object.keys(errors).length === 0;
  return (
    <form className={inDialog ? 'wc-form wc-form--dialog' : 'wc-card wc-form'} noValidate onSubmit={e => { e.preventDefault(); setTried(true); if (ok) onSubmit(draft); }}>
      {!inDialog && <span className="wc-kicker">Mitmachen</span>}
      {!inDialog && <h2 className="wc-h2">So stehst du in der Rangliste</h2>}
      <label className="wc-field">
        <span>Anzeigename</span>
        <input value={draft.displayName} maxLength={40} placeholder="Lena K." aria-invalid={tried && !!errors.displayName}
          onChange={e => setDraft(d => ({ ...d, displayName: e.target.value }))} />
        {tried && errors.displayName
          ? <small className="wc-error">{errors.displayName}</small>
          : <small className="wc-hint">{CHALLENGE_TEXTS.displayNameHint}</small>}
      </label>
      <label className="wc-field">
        <span>Deine Region</span>
        <select value={draft.bundesland} aria-invalid={tried && !!errors.bundesland}
          onChange={e => setDraft(d => ({ ...d, bundesland: e.target.value }))}>
          <option value="">Bitte wählen</option>
          {REGION_GROUPS.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.regions.map(r => <option key={r} value={r}>{displayBundesland(r)}</option>)}
            </optgroup>
          ))}
        </select>
        {tried && errors.bundesland
          ? <small className="wc-error">{errors.bundesland}</small>
          : <small className="wc-hint">{CHALLENGE_TEXTS.regionHint}</small>}
      </label>
      <p className="wc-muted" style={{ margin: 0 }}>{CHALLENGE_TEXTS.consent}</p>
      {serverError && <p className="wc-error" role="alert">{serverError}</p>}
      <div className="wc-form-actions">
        {onCancel && <button type="button" className="wc-btn wc-btn--ghost" onClick={onCancel}>Abbrechen</button>}
        <button type="submit" className="wc-btn" disabled={saving}>{saving ? 'Einen Moment …' : 'Mitmachen'}</button>
      </div>
    </form>
  );
}

/**
 * Mitmachen als Dialog über der Seite. Inline unter dem Kopf ging es unter: wer von der Anmeldung
 * zurückkam, sah das Formular nicht und suchte den Knopf ein zweites Mal (Phil, 23.10.2026).
 */
export function JoinDialog({ initial, teamName = null, teamRequest = false, serverError, saving, onSubmit, onClose }: {
  initial?: Partial<JoinDraft>;
  /** Über einen Einladungslink: nach dem Mitmachen ist man gleich in diesem Team. */
  teamName?: string | null;
  /** Ohne Link von der Teamseite: nach dem Mitmachen ist die Anfrage gestellt, nicht der Beitritt. */
  teamRequest?: boolean;
  serverError?: string | null;
  saving?: boolean;
  onSubmit: (d: JoinDraft) => void;
  onClose: () => void;
}) {
  // Fokus ins Fenster, Tab bleibt drin, Escape schließt, danach zurück auf den Knopf „Mitmachen“.
  const dialog = useDialogFocus<HTMLDivElement>(onClose);
  return (
    <div className="oa-backdrop" role="dialog" aria-modal="true" aria-labelledby="wc-join-title"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{ORGANIZER_ACCESS_CSS}</style>
      <div className="oa-dialog" ref={dialog} tabIndex={-1}>
        <div className="oa-top">
          <span className="oa-step">Winterchallenge</span>
          <button type="button" className="oa-icon-btn" onClick={onClose} aria-label="Schließen"><X size={16} /></button>
        </div>
        <h2 id="wc-join-title" className="oa-title">Mitmachen</h2>
        <p className="wc-muted" style={{ marginTop: 4 }}>
          {teamName && teamRequest
            ? <>So stehst du in der Rangliste. Danach geht deine Anfrage an <strong>{teamName}</strong>.</>
            : teamName
            ? <>So stehst du in der Rangliste. Danach fährst du gleich für <strong>{teamName}</strong>.</>
            : 'So stehst du in der Rangliste. Danach trägst du nach jeder Fahrt deine Kilometer ein.'}
        </p>
        <JoinForm inDialog initial={initial} serverError={serverError} saving={saving} onSubmit={onSubmit} onCancel={onClose} />
      </div>
    </div>
  );
}

// ── Spielregeln ───────────────────────────────────────────────────────────────────────────────

export function ChallengeRules({ summary }: { summary: ChallengeSummary }) {
  return (
    <section className="wc-card wc-rules" aria-labelledby="wc-rules-title">
      <h2 id="wc-rules-title" className="wc-h2">Spielregeln</h2>
      {/* Der Wortlaut steht einmal in `challengeRules` (shared), für Web und App. */}
      <dl>
        {challengeRules(summary).map(rule => (
          <React.Fragment key={rule.title}>
            <dt>{rule.title}</dt>
            <dd>{rule.text}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}

export function ActivityProgress({ me, summary, today }: { me: ChallengeMe; summary: ChallengeSummary; today: string }) {
  const activity = challengeActivity(me.rides, today, summary.endsOn);
  return (
    <div className="wc-activity">
      <div><strong>{formatKm(activity.weekKm)} km</strong><span>{activity.weekLabel} · {activity.weekDays} {activity.weekDays === 1 ? 'aktiver Tag' : 'aktive Tage'}</span></div>
      <div><strong>{formatKm(activity.monthKm)} km</strong><span>Im {activity.monthLabel}</span></div>
      {/* Ohne Kilometer stünde hier „0 km Rolle/Indoor · 0 %“, eine Auskunft über nichts. */}
      {me.km > 0 && <p className="wc-muted">{formatKm(activity.indoorKm)} km Rolle/Indoor · {activity.indoorPercent} % deiner Kilometer.</p>}
    </div>
  );
}
